import { useState, useCallback, type KeyboardEvent } from 'react';
import clsx from 'clsx';

interface ChatInputProps {
  onSend: (message: string) => void;
  loading?: boolean;
  disabled?: boolean;
  placeholder?: string;
}

export function ChatInput({ onSend, loading = false, disabled = false, placeholder }: ChatInputProps) {
  const [value, setValue] = useState('');

  const handleSend = useCallback(() => {
    const trimmed = value.trim();
    if (!trimmed || loading || disabled) return;
    onSend(trimmed);
    setValue('');
  }, [value, loading, onSend]);

  const handleKeyDown = useCallback(
    (e: KeyboardEvent<HTMLTextAreaElement>) => {
      if (e.key === 'Enter' && !e.shiftKey) {
        e.preventDefault();
        handleSend();
      }
    },
    [handleSend],
  );

  return (
    <div className="px-6 py-3.5 border-t border-panel-border bg-panel-bg shrink-0">
      <div className="flex gap-3">
        <textarea
          aria-label="Message"
          value={value}
          onChange={(e) => setValue(e.target.value)}
          onKeyDown={handleKeyDown}
          placeholder={placeholder ?? 'Describe your deal or add more details...'}
          disabled={loading || disabled}
          rows={1}
          className={clsx(
            'flex-1 py-3.5 px-4 bg-input-bg border border-input-border rounded-[3px]',
            'text-text text-base font-body outline-none resize-none',
            'focus:border-accent/20 transition-colors duration-200',
            'disabled:opacity-50 disabled:cursor-not-allowed',
          )}
        />
        <button
          aria-label="Send"
          onClick={handleSend}
          disabled={loading || disabled || !value.trim()}
          className={clsx(
            'w-12 h-12 rounded-[3px] border-none cursor-pointer',
            'flex items-center justify-center shrink-0',
            'transition-all duration-300 ease-[cubic-bezier(0.2,0,0,1)]',
            'hover:scale-[1.02] hover:shadow-[0_0_12px_#00c85344]',
            'disabled:opacity-50 disabled:cursor-not-allowed disabled:hover:scale-100',
          )}
          style={{
            background: 'linear-gradient(135deg, var(--color-accent), #00b848)',
          }}
        >
          {/* Transmit icon */}
          <svg width="22" height="22" viewBox="0 0 18 18" fill="none" style={{ opacity: 0.85 }}>
            <path d="M3 15L15.5 9L3 3V7.5L11 9L3 10.5V15Z" fill="#020304" />
          </svg>
        </button>
      </div>
      <div className="text-xs text-text-dim font-mono mt-2 text-center">
        Press Enter to send, Shift + Enter for new line
      </div>
    </div>
  );
}
